/** @jsx jsx */

/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';

import { useLocation, useNavigate } from 'js/lib/useRouter';

import { Divider, Grid, PageGridContainer, breakpoints } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import useIsBrowse from 'bundles/collections-common/utils/useIsBrowse';
import { usePageConfigComponentMetadata } from 'bundles/page-config-common/contexts/PageConfigComponentMetadataContext';
import AISummaryContainer from 'bundles/search-common/components/AISummaryContainer';
import NLSuggestions from 'bundles/search-common/components/NLSuggestions';
import SearchNoResultsRecommendations from 'bundles/search-common/components/SearchNoResultsRecommendations';
import SearchPageBody from 'bundles/search-common/components/SearchPageBody';
import SearchPageFilters from 'bundles/search-common/components/filters/SearchPageFilters';
import SearchExplainControls from 'bundles/search-common/components/internal-search-explainer/SearchExplainControls';
import EntityPageFeaturedSnippet from 'bundles/search-common/components/seo/EntityPageFeaturedSnippet';
import ResultsGoogleSchemaMarkup from 'bundles/search-common/components/seo/ResultsGoogleSchemaMarkup';
import SearchSEOMetatagsWriter from 'bundles/search-common/components/seo/SearchSEOMetatagsWriter';
import { ON_SITE_SEARCH_PATH, SEARCH_MAIN_INDEX_ID } from 'bundles/search-common/constants';
import { type FilterType, PAGINATION_TYPE, type PaginationType } from 'bundles/search-common/constants/filter';
import { useSearch } from 'bundles/search-common/providers/SearchContext';
import type {
  SearchFacet,
  SearchRequestOrigin,
  SearchRequestOriginPageType,
  SearchRequestOriginSegmentType,
} from 'bundles/search-common/providers/searchTypes';
import {
  getSearchEventingData,
  inferPageNumberFromParams,
  inferQueryFromParams,
} from 'bundles/search-common/utils/eventingUtils';
import {
  impressIntoBrowseFilterExperiment,
  impressIntoEQPFilterExperiment,
  impressIntoFilterExperiment,
  isInBrowseFilterTestVariant,
  isInEQPFilterTestVariant,
  isInFilterTestVariant,
  isNlSuggestionQueryPreview,
} from 'bundles/search-common/utils/experimentUtils';
import { shouldShowFreeQueryPageConfigPreview } from 'bundles/search-common/utils/pageConfigUtils';
import useIsConsumerSerp from 'bundles/search-common/utils/useIsConsumerSerp';
import useIsEqp from 'bundles/search-common/utils/useIsEqp';
import { isSearchProductHit } from 'bundles/search-common/utils/utils';

import _t from 'i18n!nls/search-common';

export type SearchFrameConfig = {
  paginationType: PaginationType;
  filterType?: FilterType;
  defaultQuery?: string;
  showCplusUpsell?: boolean;
  showSortBy?: boolean;
  showRelatedQueries?: boolean;
  isSEOEntityPage?: boolean; // TODO: extract this out to make it more generic
  enableFilters?: boolean;
  customNoResultsRenderer?: () => React.ReactNode;
  enableFeaturedSnippet?: boolean;
  enableSchemaMarkup?: boolean;
  enableSEOMetatags?: boolean;
  enableScrollToResultsOnPageChange?: boolean;
  facets?: string[];
  showResultsHeader?: boolean;
  pageType?: SearchRequestOriginPageType;
  segmentType?: SearchRequestOriginSegmentType;
};

export type SearchFrameProps = {
  config: SearchFrameConfig;
};

const styles = {
  container: css`
    padding-top: var(--cds-spacing-400);

    ${breakpoints.down('sm')} {
      padding-top: var(--cds-spacing-300);
    }

    #search-page-filters h2 {
      /* override default title for filters */
      color: var(--cds-color-grey-600);
      font-size: 16px;
      font-weight: 400;
    }
  `,
};

const getFilterCountFromParams = (
  queryParams: Record<string, string | string[] | null | undefined>,
  facets: SearchFacet[] | null | undefined
): number => {
  if (!queryParams || !facets) return 0;
  return facets?.reduce((acc, facet) => {
    let result = acc;
    if (Array.isArray(queryParams[facet.name])) {
      result += (queryParams[facet.name] as string[]).length;
    } else if (queryParams[facet.name]) {
      result += 1;
    }
    return result;
  }, 0);
};

const SearchFrame = ({ config }: SearchFrameProps) => {
  const {
    paginationType,
    filterType,
    defaultQuery,
    showCplusUpsell,
    showSortBy,
    showRelatedQueries,
    isSEOEntityPage,
    enableFilters,
    customNoResultsRenderer,
    enableFeaturedSnippet,
    enableSchemaMarkup,
    enableSEOMetatags,
    enableScrollToResultsOnPageChange,
    facets,
    showResultsHeader: showResultsHeaderConfig = true,
    pageType,
    segmentType,
  } = config;
  const pageConfigComponentMetadataContext = usePageConfigComponentMetadata();
  const index = pageConfigComponentMetadataContext?.index;
  const { getSearchResults, isLoading } = useSearch();
  const searchData = getSearchResults(SEARCH_MAIN_INDEX_ID);
  const { query: UrlQueryParams } = useLocation();
  const navigate = useNavigate();
  const { query: searchQueryFromParams, page: currentPageFromParams, isAISuggestedQuery } = UrlQueryParams;
  const inferredPageNumberFromParams: string = inferPageNumberFromParams(currentPageFromParams);
  const inferredQueryFromParams: string = inferQueryFromParams(searchQueryFromParams) || searchData?.query || '';
  const {
    query,
    facets: searchFacets,
    totalPages,
    facetFilters,
    source,
    hitsTotalCount = 0,
    inferredCurrentPageNumber = 0,
    elements = [],
    autoAppliedFilters,
    aiSearchMetadata,
  } = searchData || {};
  const isConsumerSearchPage = useIsConsumerSerp();
  const isBrowsePage = useIsBrowse();
  const isEqpPage = useIsEqp();

  if (isConsumerSearchPage) {
    impressIntoFilterExperiment();
  }
  const isAISummaryEnabled = aiSearchMetadata?.isEligibleForAiGeneratedSummary && isConsumerSearchPage;
  const shouldShowAISummary = isAISummaryEnabled || isAISuggestedQuery === 'true';
  const tracker = useTracker();

  const requestOrigin: SearchRequestOrigin | undefined =
    pageType && segmentType ? { pageType, segmentType } : undefined;
  if (isSEOEntityPage) {
    impressIntoEQPFilterExperiment();
  }
  if (isBrowsePage) {
    impressIntoBrowseFilterExperiment();
  }

  const inFilterTestVariantAndOnSearch =
    (isEqpPage && isInEQPFilterTestVariant()) ||
    (isBrowsePage && isInBrowseFilterTestVariant()) ||
    (isConsumerSearchPage && isInFilterTestVariant());
  // Simulate queryParams from config or fallback
  const queryParams = useMemo(() => ({ query: defaultQuery }), [defaultQuery]);
  const optimisticActiveFilterCount = useMemo(
    () => getFilterCountFromParams(queryParams, searchFacets),
    [queryParams, searchFacets]
  );
  // Merge autoAppliedFilters with facetFilters
  const mergedFacetFilters = useMemo(() => {
    return [...(facetFilters || []), ...(autoAppliedFilters || [])];
  }, [facetFilters, autoAppliedFilters]);

  // Build eventingData as in SearchPageRoot
  const activeFilters = useMemo(() => [...new Set(mergedFacetFilters?.flat?.() ?? [])], [mergedFacetFilters]);
  const eventingData = getSearchEventingData({
    source,
    inferredCurrentPageNumber,
    inferredPageNumberFromParams: String(inferredCurrentPageNumber),
    isInfiniteStylePagination: paginationType === PAGINATION_TYPE.INFINITE,
    activeFilters,
    searchQuery: query ?? '',
  });

  // Map config to SearchPageBody props
  const showNoResultsRecommendations = hitsTotalCount < 1 && !isLoading;
  const productSearchHits = useMemo(() => (elements ?? []).filter(isSearchProductHit), [elements]);
  // temporary override to hide header for free query experiment
  const showResultsHeader = showResultsHeaderConfig && !shouldShowFreeQueryPageConfigPreview(inferredQueryFromParams);

  const handleSuggestedQueryClick = (suggestedQuery: string) => {
    tracker('click_page_section', {
      pageSection: {
        sectionName: 'search_ai_summary',
      },
    });
    const encodedQuery = encodeURIComponent(suggestedQuery);
    const newSearchProviderUrl = `${ON_SITE_SEARCH_PATH}?query=${encodedQuery}&isAISuggestedQuery=true`;
    navigate(newSearchProviderUrl);
    tracker('perform_search', {
      searchedQuery: suggestedQuery,
      sourceLinkCategory: 'ai_summary_suggestions',
    });
  };

  return (
    <section css={styles.container} aria-label={_t('Search Results Frame')} id="search-results-frame">
      <PageGridContainer maxWidth="lg">
        <Grid container spacing={32}>
          {index !== 0 && (
            <Grid item xs={12}>
              <Divider />
            </Grid>
          )}
          {isConsumerSearchPage && <SearchExplainControls query={query || inferredQueryFromParams || ''} />}
          {/* AI Summary at the top of the page, above all other content */}
          {!showNoResultsRecommendations &&
            inferredQueryFromParams &&
            shouldShowAISummary &&
            requestOrigin &&
            source && (
              <Grid xs={12}>
                <AISummaryContainer
                  searchQuery={inferredQueryFromParams}
                  searchResults={productSearchHits}
                  onQueryClick={handleSuggestedQueryClick}
                  requestOrigin={requestOrigin}
                  source={source}
                />
              </Grid>
            )}
          {isNlSuggestionQueryPreview(inferredQueryFromParams) && (
            <Grid item xs={12}>
              <NLSuggestions onQueryClick={handleSuggestedQueryClick} />
            </Grid>
          )}
          {/* NL Suggestions - shown when preview is true and not showing AI summary */}

          {!inFilterTestVariantAndOnSearch && enableFilters && filterType && (
            <Grid item xs={12} md={3} id="search-page-filters">
              {!showNoResultsRecommendations ? (
                <SearchPageFilters
                  optimisticActiveFilterCount={optimisticActiveFilterCount}
                  eventingData={eventingData}
                  filterType={filterType}
                  showMobileSortBy={showSortBy}
                  facets={facets && facets.length > 0 ? facets : undefined}
                />
              ) : null}
            </Grid>
          )}
          <Grid item xs={12} md={inFilterTestVariantAndOnSearch || !enableFilters ? 12 : 9}>
            {showNoResultsRecommendations && (
              <>
                {customNoResultsRenderer ? (
                  customNoResultsRenderer()
                ) : (
                  <SearchNoResultsRecommendations eventingData={eventingData} />
                )}
              </>
            )}
            {!showNoResultsRecommendations && (
              <SearchPageBody
                optimisticActiveFilterCount={optimisticActiveFilterCount}
                isSEOEntityPageWithQuery={!!isSEOEntityPage}
                eventingData={eventingData}
                paginationType={paginationType}
                searchQuery={inferredQueryFromParams}
                page={Number(inferredPageNumberFromParams)}
                showCplusUpsell={showCplusUpsell}
                showDesktopSortBy={showSortBy && !inFilterTestVariantAndOnSearch}
                showRelatedQueries={showRelatedQueries}
                enableScrollToResultsOnPageChange={enableScrollToResultsOnPageChange}
                enableFilters={enableFilters}
                showResultsHeader={showResultsHeader}
                facets={facets && facets.length > 0 ? facets : undefined}
                shouldShowAllResultsText={shouldShowAISummary}
              />
            )}
          </Grid>
        </Grid>
        {enableFeaturedSnippet && (
          <EntityPageFeaturedSnippet
            isSEOEntityPage={!!isSEOEntityPage}
            searchQuery={query || ''}
            searchResults={productSearchHits}
          />
        )}
      </PageGridContainer>
      {enableSchemaMarkup && <ResultsGoogleSchemaMarkup hits={productSearchHits} />}
      {enableSEOMetatags && <SearchSEOMetatagsWriter hits={productSearchHits} totalPages={totalPages} />}
    </section>
  );
};

export default SearchFrame;
